import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { FileText } from 'lucide-react';
import { ProcurementProfileData, AccountFinal } from '@/api/entities';
import { useTranslation } from '@/components/utils/translations';
import { translateProcurementFriendliness } from '@/lib/accountClassificationLabels';

// Australia data
const dataAustralia = [
  { name: "Standard", value: 41.9, color: "#1C485C" },
  { name: "Friendly", value: 37.2, color: "#00C2C2" },
  { name: "Bureaucratic", value: 20.9, color: "#89CDD4" }
];

// Hong Kong data - all 11 universities
const dataHongKong = [
  { name: "Standard", value: 63.6, color: "#1C485C" }, // 7 universities
  { name: "Friendly", value: 27.3, color: "#00C2C2" }, // 3 universities
  { name: "Bureaucratic", value: 9.1, color: "#89CDD4" } // 1 university
];

// South Africa data
const dataSouthAfrica = [
  { name: "Standard", value: 61.5, color: "#1C485C" },
  { name: "Friendly", value: 30.8, color: "#00C2C2" },
  { name: "Bureaucratic", value: 7.7, color: "#89CDD4" }
];

const segmentMeanings = {
  "Friendly": "Transparent process, fast decisions, open to pilot procurement",
  "Standard": "Typical university process (open RFPs, known timelines)",
  "Bureaucratic": "Framework-bound, rigid, or government-controlled procurement",
  "Monitor": "Relationship-driven, heavier documentation, less transparent commercial terms"
};

const commentaryAustralia = "Most institutions follow a Standard process (42%), while 37% are Friendly and open to pilots or direct awards. Only 21% remain Bureaucratic, showing Australia's higher-ed sector is largely accessible to new vendors.";

const commentaryHongKong = "Hong Kong's procurement landscape is more structured: 64% follow Standard processes with clear timelines and RFPs, 27% are Friendly to pilots and direct awards (especially private institutions like HKBU and SFU), and only 9% are Bureaucratic. The UGC-funded majors (HKU, CUHK, HKUST) lean Standard but have proven responsive to strategic partnerships.";

const commentaryUK = "The UK higher education procurement landscape is largely structured and accessible: most institutions (≈60%) follow Standard processes with clear RFPs and timelines, while ≈35% are Friendly with streamlined routes for pilots and direct awards. Only ≈5% remain Bureaucratic, showing the UK sector is generally vendor-friendly with established frameworks like UKUPC and SUPC enabling efficient procurement.";

const commentarySouthAfrica = "South Africa's procurement landscape is moderately accessible: 62% of institutions follow Standard processes with established RFP procedures, 31% are Friendly and open to direct awards or pilots, and only 8% remain Bureaucratic with rigid government-controlled procurement. This distribution reflects a sector balancing regulatory compliance with vendor accessibility.";

const commentarySkillwell = "Enterprise procurement across these Fortune 500 accounts is predominantly Standard (71%), following established BU and division-led processes with clear timelines. Monitor accounts (29%) require more careful navigation through subcontracting or formal RFP routes. No accounts are classified as Bureaucratic, reflecting the enterprise sector's preference for agile, pilot-driven procurement over rigid frameworks.";

const dataLearnwise = [
  { name: "Standard", value: 60, color: "#1C485C" },
  { name: "Monitor", value: 40, color: "#D1D5DB" }
];

const commentaryLearnwise = "Around 60% of the portfolio has Standard procurement accessibility — visible tender portals, published thresholds, and navigable processes that an external vendor can engage through with preparation. The remaining ~40% sit in Monitor territory, where vendor entry is more relationship-driven, documentation burdens are heavier, and commercial terms are less transparent, requiring longer lead times and often a local partner. No accounts are fully frictionless or fully closed — the portfolio sits in a workable middle ground, with the key differentiator being whether engagement is portal-led or relationship-led.";

const dataPlataformaa = [
  { name: "Mexico University Tenders", value: 33, color: "#0A1A2F" },
  { name: "Colombia SECOP Platform", value: 33, color: "#1C485C" },
  { name: "Peru SEACE Portal", value: 13, color: "#3D8C91" },
  { name: "Chile Mercado Público", value: 13, color: "#56B0AF" },
  { name: "Private/Internal", value: 7, color: "#89CDD4" }
];

const commentaryPlataformaa = "Procurement in Latin American higher education is dominated by public tender platforms: 33% route through Mexico's university-level public tenders (UNAM, UdeG, UAEMéx, BUAP), 33% via Colombia's SECOP I/II centralized system, 13% each through Peru's SEACE portal and Chile's Mercado Público. Only 7% use private internal procurement (UPC). Pattern: public universities across all four countries mandate formal tender processes with significant documentation and compliance requirements, creating high barriers to entry for international vendors unfamiliar with country-specific platforms.";

const CustomTooltip = ({ active, payload, t, language }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    const displayName = translateProcurementFriendliness(language, data.name);
    return (
      <div className="bg-[#0A1A2F] text-white p-3 rounded-lg shadow-lg max-w-xs border border-[#00C2C2]">
        <p className="font-semibold text-sm mb-1">{displayName}</p>
        <p className="text-xs text-[#00C2C2] mb-2">{data.value}% of institutions</p>
        <p className="text-xs italic">{segmentMeanings[data.name]}</p>
      </div>
    );
  }
  return null;
};

export default function ProcurementComplexity({ market }) {
  const { t, language } = useTranslation();
  const [dataUK, setDataUK] = useState(null);
  const [dataSA, setDataSA] = useState(null);
  const [dataSkillwell, setDataSkillwell] = useState(null);
  const [dataKairadigital, setDataKairadigital] = useState(null);
  const [dataWayground, setDataWayground] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const isHongKong = market === 'hong-kong';
  const isUK = market === 'united-kingdom';
  const isSouthAfrica = market === 'south-africa';
  const isSkillwell = market === 'skillwell';
  const isLearnwise = market === 'learnwise';
  const isKairadigital = market === 'kairadigital';
  const isWayground = market === 'wayground';
  const isPlataformaa = market === 'plataformaa';
  
  useEffect(() => {
    if (isUK && !dataUK) {
      loadUKData();
    } else if (isSouthAfrica && !dataSA) {
      loadSouthAfricaData();
    } else if (isSkillwell && !dataSkillwell) {
      loadSkillwellData();
    } else if (isKairadigital && !dataKairadigital) {
      loadKairadigitalData();
    } else if (isWayground && !dataWayground) {
      loadWaygroundData();
    }
  }, [isUK, isSouthAfrica, isSkillwell, isKairadigital, isWayground, dataUK, dataSA, dataSkillwell, dataKairadigital, dataWayground]);
  
  const loadUKData = async () => {
    setIsLoading(true);
    try {
      const profiles = await ProcurementProfileData.list();
      
      // Count procurement_friendliness values
      const counts = { Friendly: 0, Standard: 0, Bureaucratic: 0, Monitor: 0 };
      let total = 0;
      
      profiles.forEach(profile => {
        if (!profile.profile_data) return;
        
        // Look for procurement_friendliness in the profile_data structure
        const sections = profile.profile_data;
        
        // Check section "5 Policy currency & transparency" or wherever it might be
        for (const sectionKey in sections) {
          const section = sections[sectionKey];
          if (Array.isArray(section)) {
            section.forEach(field => {
              if (field.field === 'Procurement friendliness' && field.value) {
                const value = field.value.trim();
                if (counts.hasOwnProperty(value)) {
                  counts[value]++;
                  total++;
                }
              }
            });
          }
        }
      });
      
      if (total === 0) {
        // Fallback: use estimated UK distribution if no data
        setDataUK([
          { name: "Standard", value: 60.0, color: "#1C485C" },
          { name: "Friendly", value: 35.0, color: "#00C2C2" },
          { name: "Bureaucratic", value: 5.0, color: "#89CDD4" }
        ]);
      } else {
        // Calculate percentages (excluding Monitor as it's not a complexity level)
        const complexityTotal = counts.Friendly + counts.Standard + counts.Bureaucratic;
        
        if (complexityTotal > 0) {
          setDataUK([
            { name: "Standard", value: (counts.Standard / complexityTotal) * 100, color: "#1C485C" },
            { name: "Friendly", value: (counts.Friendly / complexityTotal) * 100, color: "#00C2C2" },
            { name: "Bureaucratic", value: (counts.Bureaucratic / complexityTotal) * 100, color: "#89CDD4" }
          ].filter(item => item.value > 0));
        } else {
            setDataUK([
              { name: "Standard", value: 60.0, color: "#1C485C" },
              { name: "Friendly", value: 35.0, color: "#00C2C2" },
              { name: "Bureaucratic", value: 5.0, color: "#89CDD4" }
            ]);
        }
      }
    } catch (error) {
      console.error('Error loading UK procurement data:', error);
      // Fallback to estimated distribution
      setDataUK([
        { name: "Standard", value: 60.0, color: "#1C485C" },
        { name: "Friendly", value: 35.0, color: "#00C2C2" },
        { name: "Bureaucratic", value: 5.0, color: "#89CDD4" }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSouthAfricaData = async () => {
    setIsLoading(true);
    try {
      const accounts = await AccountFinal.filter({
        tenant_id: { $in: ['smowl'] },
        country: 'South Africa'
      });
      
      const counts = { Friendly: 0, Standard: 0, Bureaucratic: 0 };
      let total = 0;
      
      accounts.forEach(account => {
        const friendliness = account.procurement_friendliness;
        if (friendliness && counts.hasOwnProperty(friendliness)) {
          counts[friendliness]++;
          total++;
        }
      });
      
      if (total === 0) {
        setDataSA(dataSouthAfrica); // Fallback to hardcoded data if no accounts match
      } else {
        // Only include segments with values greater than 0
        const calculatedData = [
          { name: "Standard", value: (counts.Standard / total) * 100, color: "#1C485C" },
          { name: "Friendly", value: (counts.Friendly / total) * 100, color: "#00C2C2" },
          { name: "Bureaucratic", value: (counts.Bureaucratic / total) * 100, color: "#89CDD4" }
        ].filter(item => item.value > 0);

        // Ensure at least one segment is present, if not, fallback
        if (calculatedData.length > 0) {
            setDataSA(calculatedData);
        } else {
            setDataSA(dataSouthAfrica);
        }
      }
    } catch (error) {
      console.error('Error loading South Africa procurement data:', error);
      setDataSA(dataSouthAfrica); // Fallback to hardcoded data on error
    } finally {
      setIsLoading(false);
    }
  };

  const loadSkillwellData = async () => {
    setIsLoading(true);
    try {
      const accounts = await AccountFinal.filter({
        tenant_id: 'skillwell'
      });
      
      const counts = { Friendly: 0, Standard: 0, Bureaucratic: 0, Monitor: 0 };
      let total = 0;
      
      accounts.forEach(account => {
        const friendliness = account.procurement_friendliness;
        if (friendliness && counts.hasOwnProperty(friendliness)) {
          counts[friendliness]++;
          total++;
        }
      });
      
      if (total === 0) {
        // Fallback based on provided data: 10 Standard, 4 Monitor
        setDataSkillwell([
          { name: "Standard", value: 71.4, color: "#1C485C" },
          { name: "Monitor", value: 28.6, color: "#D1D5DB" }
        ]);
      } else {
        const calculatedData = [
          { name: "Standard", value: (counts.Standard / total) * 100, color: "#1C485C" },
          { name: "Friendly", value: (counts.Friendly / total) * 100, color: "#00C2C2" },
          { name: "Bureaucratic", value: (counts.Bureaucratic / total) * 100, color: "#89CDD4" },
          { name: "Monitor", value: (counts.Monitor / total) * 100, color: "#D1D5DB" }
        ].filter(item => item.value > 0);

        if (calculatedData.length > 0) {
          setDataSkillwell(calculatedData);
        } else {
          setDataSkillwell([
            { name: "Standard", value: 71.4, color: "#1C485C" },
            { name: "Monitor", value: 28.6, color: "#D1D5DB" }
          ]);
        }
      }
    } catch (error) {
      console.error('Error loading Skillwell procurement data:', error);
      setDataSkillwell([
        { name: "Standard", value: 71.4, color: "#1C485C" },
        { name: "Monitor", value: 28.6, color: "#D1D5DB" }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadKairadigitalData = async () => {
    setIsLoading(true);
    try {
      const accounts = await AccountFinal.filter({
        tenant_id: 'kairadigital'
      });
      
      const counts = { Friendly: 0, Standard: 0, Bureaucratic: 0, Monitor: 0 };
      let total = 0;
      
      accounts.forEach(account => {
        const friendliness = account.procurement_friendliness;
        if (friendliness && counts.hasOwnProperty(friendliness)) {
          counts[friendliness]++;
          total++;
        }
      });
      
      if (total === 0) {
        // Fallback if no data
        setDataKairadigital([
          { name: "Standard", value: 100.0, color: "#1C485C" }
        ]);
      } else {
        const calculatedData = [
          { name: "Standard", value: (counts.Standard / total) * 100, color: "#1C485C" },
          { name: "Friendly", value: (counts.Friendly / total) * 100, color: "#00C2C2" },
          { name: "Bureaucratic", value: (counts.Bureaucratic / total) * 100, color: "#89CDD4" },
          { name: "Monitor", value: (counts.Monitor / total) * 100, color: "#D1D5DB" }
        ].filter(item => item.value > 0);

        if (calculatedData.length > 0) {
          setDataKairadigital(calculatedData);
        } else {
          setDataKairadigital([
            { name: "Standard", value: 100.0, color: "#1C485C" }
          ]);
        }
      }
    } catch (error) {
      console.error('Error loading Kairadigital procurement data:', error);
      setDataKairadigital([
        { name: "Standard", value: 100.0, color: "#1C485C" }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadWaygroundData = async () => {
    setIsLoading(true);
    try {
      const accounts = await AccountFinal.filter({
        tenant_id: 'wayground'
      });
      
      const counts = { Friendly: 0, Standard: 0, Bureaucratic: 0 };
      let total = 0;
      
      accounts.forEach(account => {
        const friendliness = account.procurement_friendliness;
        if (friendliness && counts.hasOwnProperty(friendliness)) {
          counts[friendliness]++;
          total++;
        }
      });
      
      if (total === 0) {
        // Fallback if no data
        setDataWayground([
          { name: "Standard", value: 100.0, color: "#1C485C" }
        ]);
      } else {
        const calculatedData = [
          { name: "Standard", value: (counts.Standard / total) * 100, color: "#1C485C" },
          { name: "Friendly", value: (counts.Friendly / total) * 100, color: "#00C2C2" },
          { name: "Bureaucratic", value: (counts.Bureaucratic / total) * 100, color: "#89CDD4" }
        ].filter(item => item.value > 0);

        if (calculatedData.length > 0) {
          setDataWayground(calculatedData);
        } else {
          setDataWayground([
            { name: "Standard", value: 100.0, color: "#1C485C" }
          ]);
        }
      }
    } catch (error) {
      console.error('Error loading Wayground procurement data:', error);
      setDataWayground([
        { name: "Standard", value: 100.0, color: "#1C485C" }
      ]);
    } finally {
      setIsLoading(false);
    }
  };
  
  let data, commentary;
  
  if (isPlataformaa) {
    data = dataPlataformaa;
    commentary = commentaryPlataformaa;
  } else if (isWayground) {
    data = dataWayground || [
      { name: "Standard", value: 100.0, color: "#1C485C" }
    ];
    commentary = "Public procurement in Latin America is predominantly Bureaucratic, governed by state-level frameworks and mandatory electronic tender portals. While transparency is high through comprehensive procurement platforms, the process is often rigid with extended timelines and strict regulatory compliance requirements. Educational institutions typically follow state-mandated procurement laws with limited flexibility for pilot programs or direct awards.";
  } else if (isKairadigital) {
    data = dataKairadigital || [
      { name: "Standard", value: 100.0, color: "#1C485C" }
    ];
    commentary = "Procurement across these global enterprises is primarily driven by business-function and division-led pilots that enable agile, champion-driven market entry. Accounts follow structured evaluation processes but remain accessible to vendors who can demonstrate clear value through targeted technical proof-of-concepts.";
  } else if (isSkillwell) {
    data = dataSkillwell || [
      { name: "Standard", value: 71.4, color: "#1C485C" },
      { name: "Monitor", value: 28.6, color: "#D1D5DB" }
    ];
    commentary = commentarySkillwell;
  } else if (isLearnwise) {
    data = dataLearnwise;
    commentary = commentaryLearnwise;
  } else if (isUK) {
    data = dataUK || [
      { name: "Standard", value: 60.0, color: "#1C485C" },
      { name: "Friendly", value: 35.0, color: "#00C2C2" },
      { name: "Bureaucratic", value: 5.0, color: "#89CDD4" }
    ];
    commentary = commentaryUK;
  } else if (isSouthAfrica) {
    data = dataSA || dataSouthAfrica;
    commentary = commentarySouthAfrica;
  } else if (isHongKong) {
    data = dataHongKong;
    commentary = commentaryHongKong;
  } else {
    data = dataAustralia;
    commentary = commentaryAustralia;
  }
  
  return (
    <Card className="border-0 shadow-md h-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#00C2C2]" />
          <CardTitle className="text-lg font-semibold text-[#0A1A2F]">{t('procurementComplexity')}</CardTitle>
        </div>
        <p className="text-sm text-[#5C6B7A]">{t('distributionProfiles')}</p>
        {isUK && (
          <p className="text-xs text-[#5C6B7A] italic mt-1">(data is based on 16 non-Canvas universities)</p>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[280px] md:h-[350px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C2C2]"></div>
          </div>
        ) : (
          <div className="h-[280px] md:h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${translateProcurementFriendliness(language, name)} (${(percent * 100).toFixed(1)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={(props) => <CustomTooltip {...props} t={t} language={language} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-[#5C6B7A] leading-relaxed">
            {commentary}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}